import { useEffect, useRef, useState } from 'react';
import type { AddressRecord } from '@yeabe/addressbook';
import { IconPin } from './NavIcons';
import { Notice, Spinner } from './ui';
import { BTN_SOFT, BTN_SOFT_STYLE } from '../lib/ui';

const DIAL_CODES: Record<string, string> = { IN: '+91', US: '+1', GB: '+44', SG: '+65', AE: '+971', AU: '+61', CA: '+1', DE: '+49', FR: '+33' };

/** Yeabe wants the national number (digits only) plus a dial code. */
function splitPhone(raw: string | null | undefined, countryCode: string): { phone?: string; countryCode: string } {
  const fallback = DIAL_CODES[countryCode] || '+91';
  if (!raw) return { countryCode: fallback };
  const trimmed = raw.trim();
  const digits = trimmed.replace(/\D/g, '');
  if (trimmed.startsWith('+')) {
    const dial = Object.values(DIAL_CODES)
      .sort((a, b) => b.length - a.length)
      .find((d) => digits.startsWith(d.slice(1)));
    if (dial) return { phone: digits.slice(dial.length - 1), countryCode: dial };
  }
  const dialDigits = fallback.slice(1);
  const national = digits.length > 10 && digits.startsWith(dialDigits) ? digits.slice(dialDigits.length) : digits;
  return { phone: national || undefined, countryCode: fallback };
}

export type PickedAddress = {
  addressLine: string;
  addressLine2: string;
  city: string;
  stateCode: string;
  stateName: string;
  postalCode: string;
  countryCode: string;
  yeabeAddressId: string;
};

function toPicked(a: AddressRecord): PickedAddress {
  return {
    addressLine: [a.building_number, a.street_line_1].filter(Boolean).join(', '),
    addressLine2: a.street_line_2 || '',
    city: a.city || a.city_name || '',
    stateCode: a.state_code || '',
    stateName: a.state_name || '',
    postalCode: a.postal_code || '',
    countryCode: (a.country_code || '').toUpperCase(),
    yeabeAddressId: a.id,
  };
}

type Props = {
  email: string;
  phone: string | null | undefined;
  countryCode: string;
  linked: boolean;
  disabled?: boolean;
  onPick: (address: PickedAddress) => void;
};

export default function YeabeAddressPicker({ email, phone, countryCode, linked, disabled, onPick }: Props) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const hasContact = Boolean(email || phone);

  useEffect(() => {
    if (!open || !hostRef.current) return;
    let destroy: (() => void) | undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    import('@yeabe/addressbook')
      .then(({ AddressBook }) => {
        if (cancelled || !hostRef.current) return;
        const book = new AddressBook({
          entity: 'Micus',
          communicationEmail: import.meta.env.VITE_YEABE_COMMUNICATION_EMAIL || 'one@lab63.org',
        });
        const split = splitPhone(phone, countryCode);
        destroy = book.launch(hostRef.current, {
          ...split,
          email: email || undefined,
          allowAdd: true,
          style: 'magenta',
          title: 'Choose the registered company address',
          columns: 1,
          onSelect: (a) => {
            onPick(toPicked(a));
            setOpen(false);
          },
          onAddressAdded: (a) => {
            onPick(toPicked(a));
            setOpen(false);
          },
          onDismiss: () => setOpen(false),
        });
      })
      .catch(() => setError('Could not load the Yeabe address book. Enter the address manually below.'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
      destroy?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <div className="rounded-2xl p-4" style={{ background: 'linear-gradient(135deg,#fff5f8 0%,#fdf2f8 100%)', border: '1px solid #fce7f3' }}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-[#d6006e]" style={{ border: '1px solid #fce7f3' }}>
          <IconPin className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-[#6b0030]">{linked ? 'Address linked from Yeabe' : 'Use your Yeabe address book'}</p>
          <p className="text-xs text-[#b0708f]">
            {linked
              ? 'Verified address pulled from Yeabe. Edit any field below to switch to a manual address.'
              : 'Pick a saved, verified address (or add one) with your email / phone — no retyping.'}
          </p>
        </div>
        <button type="button" disabled={disabled || !hasContact} onClick={() => setOpen(true)} className={BTN_SOFT} style={BTN_SOFT_STYLE}>
          {linked ? 'Change address' : 'Open Yeabe'}
        </button>
      </div>
      {!hasContact && <p className="mt-2 text-[11px] text-[#c084a0]">Add a company email or phone above to look up Yeabe addresses.</p>}
      {error && (
        <div className="mt-3">
          <Notice tone="amber">{error}</Notice>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Yeabe address book">
          <button type="button" aria-label="Close" className="absolute inset-0 bg-[#2b0018]/55 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="mz-pop relative max-h-[88vh] w-full max-w-xl overflow-y-auto rounded-3xl bg-white p-4 shadow-2xl">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#c084a0]">Yeabe address book</p>
              <button type="button" onClick={() => setOpen(false)} className="rounded-lg px-2 py-1 text-xs font-bold text-[#d6006e] hover:bg-[#fdf2f8]">
                Close
              </button>
            </div>
            {loading && (
              <div className="flex items-center gap-2 py-8 text-sm text-[#c084a0]">
                <Spinner /> Loading Yeabe…
              </div>
            )}
            <div ref={hostRef} />
          </div>
        </div>
      )}
    </div>
  );
}
